import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import clsx from 'clsx';
import _get from 'lodash/get';

import { getPathSRP } from '@/utilities';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import {
    Button,
    Col,
    Glyphicon,
    IconButton,
    Image,
    Row,
    Text,
} from 'reaxl';

import remoteActions from '@/actions/srp/remoteActions';

import {
    authDuck,
    queryDuck,
    userDuck,
} from '@/ducks';

import { srpActiveInteractionDuck, srpContentDuck } from '@/ducks/srp';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import InventoryClearFilters from './InventoryClearFilters';
import KeywordSearchContainer from './KeywordSearchContainer';
import SaveSearchContainer from './SaveSearchContainer';
import SrpHeroContainer from './SrpHeroContainer';
import SuggestedFilterContainer from './SuggestedFilterContainer';

function KeywordButton({ onClick, label = 'Search by keyword' }) {
    return (
        <IconButton
            label={label}
            glyph="search"
            hideLabel
            size="large"
            onClick={onClick}
        />
    );
}

export function SearchResultsTitleContainer() {
    const dispatch = useDispatch();

    const device = useDevice();
    const lessThanMd = _get(device, 'lessThan.md', false);
    const isXs = _get(device, 'is.xs', false);

    const { locationHeading } = useSelector(srpContentDuck.selectors.getDuckState);
    const showKeyWordSearchModal = useSelector(srpActiveInteractionDuck.selectors.getShowKeywordSearchModal);

    const {
        bookmarkId = null,
        city = '',
        dma = {},
        location = {},
        searchBookmarkId = null,
        sellerType = '',
        state = '',
        zip = '',
    } = useSelector(queryDuck.selectors.getDuckState) || {};

    const savedSearch = useSelector(userDuck.selectors.getSavedSearch) || {};
    const savedSearchTitle = savedSearch.title;
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);

    const [keywordExpand, setKeywordExpand] = useState(false);
    const navigateToSrp = useSrpNavigation();

    const [backToResultsPath, setPath] = useState('');

    const {
        // feature configs
        advanced_keyword_search: [enableAdvancedKeywordSearch],
        srp_category_search: [enableCategorySearch, { suggested_filters: enableSuggestedFilters }],
        srp_hero: [enableSrpHero, {
            alt_text: altText,
            background_color: backgroundColor,
            cmp: heroCmpId,
            hero_height: heroHeight,
            src: heroSrc,
            logo: logoSrc,
        }],
        suggested_keyword: [enableKeyword],
    } = useFeatures([
        'advanced_keyword_search',
        'enable_private_seller_exchange_logo',
        'srp_category_search',
        'srp_hero',
        'personalization',
        'psx_logo',
        'suggested_keyword',
    ]);

    useEffect(() => {
        if (searchBookmarkId && isLoggedIn) {
            dispatch(remoteActions.loadSavedSearchBookmark());
        }
        if (bookmarkId) {
            dispatch(remoteActions.handleSearchRedirect(false, navigateToSrp));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchBookmarkId]);

    // sets path for cta in SRP hero
    useEffect(() => {
        const getBackToResultsPath = async () => {
            const path = await getPathSRP({ city, dma, experience: null, location, state, zip }, { basePath: true });
            setPath(path);
        };

        if (enableSrpHero) {
            getBackToResultsPath();
        }
    }, [city, dma, enableSrpHero, location, state, zip]);

    const renderFilterChipsControls = (
        <div className="display-flex flex-nowrap gap-3">
            <SaveSearchContainer
                key="saveSearchContainer"
            />
            <InventoryClearFilters
                key="clearFilters"
            />
        </div>
    );

    const handleKeywordExpand = () => {
        setKeywordExpand(!keywordExpand);
    };

    const handleShowKeywordModal = () => dispatch(srpActiveInteractionDuck.creators.setKeys({
        showKeywordSearchModal: true,
    }));

    const renderKeywordBlock = (
        <>
            <div className="w-100 margin-left-2"><KeywordSearchContainer /></div>
            <div className="align-self-center">
                <Button
                    bsStyle="link"
                    onClick={handleKeywordExpand}
                    className="padding-left-2"
                >
                    <Glyphicon
                        glyph="remove"
                        className="text-gray-darkest"
                    />
                </Button>
            </div>
        </>
    );

    const heroContent = (
        <SrpHeroContainer
            backToResultsPath={backToResultsPath}
            locationHeading={locationHeading}
            savedSearchTitle={savedSearchTitle}
            sellerType={sellerType}
        />
    );

    const backgroundPosition = useMemo(() => {
        if (heroHeight) {
            return 'center';
        }
        if (lessThanMd) {
            return 'right 10px top 10px';
        }
        return 'center right';
    }, [heroHeight, lessThanMd]);

    return (
        <>
            <div className={clsx(backgroundColor)}>
                <div
                    className="container padding-vertical-3"
                    data-cmp={heroCmpId + '-srp-hero'}
                    style={{
                        backgroundImage: `url(${heroSrc})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition,
                        backgroundSize: !heroHeight && (lessThanMd ? '100px' : 'auto 90%'),
                        height: heroHeight,
                    }}
                >
                    {!heroHeight && heroContent}
                    {heroHeight && (

                        <div className="display-flex flex-wrap justify-content-center">
                            <Text
                                className="w-100 text-center"
                                color="subdued-light"
                                size={100}
                            >
                                Sponsored by
                            </Text>
                            <div className="display-md-flex justify-content-center align-items-top gap-2 text-center">
                                <div>
                                    <Image
                                        src={logoSrc}
                                        alt={altText}
                                        lazyload={false}
                                        loading="eager"
                                        width={140}
                                    />
                                </div>
                                <Text
                                    color="subdued-light"
                                    size={400}
                                    weight="bold"
                                    componentClass="div"
                                >
                                    Buy Online Store
                                </Text>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {heroHeight && (
                <div className="container margin-vertical-3 padding-md-0">
                    {heroContent}
                </div>
            )}

            {(!enableCategorySearch || enableCategorySearch && enableSuggestedFilters) && isXs && (
                <div className="container padding-sm-0 margin-vertical-sm-3">
                    <Row className="display-flex align-items-center justify-content-between padding-vertical-1 padding-vertical-sm-0">
                        {!isXs && (
                            <KeywordSearchContainer
                                className="col-sm-5 col-md-3"
                            />
                        )}

                        <Col
                            xs={12}
                            sm={enableKeyword ? 7 : 12}
                            md={enableKeyword ? 9 : 12}
                            className={clsx('padding-horizontal-0 padding-horizontal-sm-2', {
                                'padding-vertical-1': enableKeyword && keywordExpand,
                            })}
                        >
                            <div
                                className="display-flex justify-items-center align-items-center flex-direction-row w-100"
                            >
                                {!enableAdvancedKeywordSearch && (
                                    <>
                                        {enableKeyword && isXs && !keywordExpand && <KeywordButton onClick={handleKeywordExpand} />}
                                        {enableKeyword && isXs && keywordExpand && renderKeywordBlock}
                                    </>
                                )}
                                {enableAdvancedKeywordSearch && (
                                    <>
                                        {enableKeyword && isXs && <KeywordButton onClick={handleShowKeywordModal} />}
                                        {enableKeyword && isXs && showKeyWordSearchModal && <KeywordSearchContainer showInModal /> }
                                    </>
                                )}
                                {!lessThanMd && renderFilterChipsControls}
                                {!keywordExpand && (
                                    <div
                                        className="padding-top-sm-0"
                                        style={{ minWidth: '100px' }}
                                    >
                                        <SuggestedFilterContainer />
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </div>
            )}
        </>
    );
}

export default SearchResultsTitleContainer;
